import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import './App.css'
import { AuthProvider, useAuth } from './context/AuthContext'
import { DeviceProvider } from './context/DeviceContext'
import Mixpanel from './components/analytics/Mixpanel'
import Highlight from './components/analytics/Highlight'

import ResponsiveLayout from './components/navigation/ResponsiveLayout';
import { UserProfile } from './components/user-profile';
import { CharacterProfile } from './components/character-profile';
import Persona from './components/Persona';
import { Privacy, Terms } from './components/legal';
import Chats from './components/Chats';
import { Explore } from './components/explore';
import { Create, CreateCharacter, CreateScenario } from './components/create';
import { ActiveChat } from './components/active-chat';
import Login from './components/shared/Login';
import { GoogleOAuthCallback, AppleOAuthCallback } from './components/shared/OAuthCallback';
import OpenGraphPreview from './components/shared/OpenGraphPreview';

import Scenario from './components/scenario/Scenario';
import ActiveScenarioSession from './components/scenario/ActiveScenarioSession';
import World from './components/world/World';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps): JSX.Element | null {
  const { isLoggedIn, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

function App(): JSX.Element {
  return (
    <AuthProvider>
      <DeviceProvider>
      <BrowserRouter>
        <Mixpanel />
        <Highlight />
        <Routes>
          <Route path="/" element={<Navigate to="/explore" replace />} />
          
          <Route path="/chats" element={
            <ProtectedRoute>
              <ResponsiveLayout>
                <Chats />
              </ResponsiveLayout>
            </ProtectedRoute>
          } />
    
          <Route path="/explore" element={
            <ResponsiveLayout>
              <Explore />
            </ResponsiveLayout>
          } />
          
          <Route path="/create" element={
            <ProtectedRoute>
              <ResponsiveLayout>
                <Create />
              </ResponsiveLayout>
            </ProtectedRoute>
          } />
          
          <Route path="/create-character" element={
            <ProtectedRoute>
              <ResponsiveLayout>
                <CreateCharacter />
              </ResponsiveLayout>
            </ProtectedRoute>
          } />

          <Route path="/create-scenario" element={
            <ProtectedRoute>
              <ResponsiveLayout>
                <CreateScenario />
              </ResponsiveLayout>
            </ProtectedRoute>
          } />

          <Route path="/chats/:chatId" element={<ActiveChat />} />
          <Route path="/chats-preview/:username" element={<ActiveChat />} />
          <Route path="/users/:username" element={
            <ResponsiveLayout fullScreen>
              <UserProfile />
            </ResponsiveLayout>
          } />
          <Route path="/chars/:username" element={
            <ResponsiveLayout fullScreen>
              <CharacterProfile />
            </ResponsiveLayout>
          } />
          <Route path="/personas/:personaId" element={<Persona />} />

          <Route path="/scenarios/:scenarioId" element={<Scenario />} />
          <Route path="/sessions/:scenarioId" element={<ActiveScenarioSession />} />
          <Route path="/worlds/:slug" element={<World />} />
    
          <Route path="/preview/:type/:lookupId" element={<OpenGraphPreview />} />
          
          <Route path="/login" element={<Login />} />
          <Route path="/oauth/google" element={<GoogleOAuthCallback />} />
          <Route path="/oauth/apple" element={<AppleOAuthCallback />} />

          <Route path="/privacy" element={<ResponsiveLayout><Privacy /></ResponsiveLayout>} />
          <Route path="/terms" element={<ResponsiveLayout><Terms /></ResponsiveLayout>} />
        </Routes>
      </BrowserRouter>
    </DeviceProvider>
    </AuthProvider>
  )
}

export default App